import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { formatApiError } from '../lib/apiError';
import { assertOnlineOrThrow } from '../lib/offlineGuard';
import { dispatchChatUnreadRefresh } from '../lib/chatEvents';

function sb() {
  const client = getSupabase();
  if (!client) throw new Error('SUPABASE_MISSING');
  return client;
}

export async function createBusinessInquiryRoom(businessId, catalogItemId = null) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('create_chat_room', {
    p_room_type: 'business_inquiry',
    p_business_id: businessId,
    p_catalog_item_id: catalogItemId,
    p_target_user_id: null,
    p_travel_group_id: null,
  });
  if (error) throw new Error(formatApiError(error));
  return data;
}

export async function createDirectRoom(targetUserId) {
  return createOrGetDirectRoom(targetUserId);
}

/** DM 방 생성·재사용 (slice 3). context_id null → [DM] 탭. returns room_id uuid */
export async function createOrGetDirectRoom(otherUserId) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('create_or_get_direct_room', {
    p_other_user_id: otherUserId,
  });
  if (error) {
    const msg = formatApiError(error);
    if (/chat_blocked/i.test(msg) || error.message === 'chat_blocked') {
      const e = new Error('CHAT_BLOCKED');
      e.code = 'CHAT_BLOCKED';
      throw e;
    }
    throw new Error(msg);
  }
  return data;
}

/** 거래 채팅방 생성·재사용 (slice 1 product · slice 2 property). returns room_id uuid */
export async function createOrGetTradeRoom(contextType, contextId, sellerUserId) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('create_or_get_trade_room', {
    p_context_type: contextType,
    p_context_id: contextId,
    p_seller_user_id: sellerUserId,
  });
  if (error) {
    const msg = formatApiError(error);
    if (/chat_blocked/i.test(msg) || error.message === 'chat_blocked') {
      const e = new Error('CHAT_BLOCKED');
      e.code = 'CHAT_BLOCKED';
      throw e;
    }
    throw new Error(msg);
  }
  return data;
}

export async function sendMessage(roomId, messageType = 'text', payload = {}) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('send_message', {
    p_room_id: roomId,
    p_message_type: messageType,
    p_payload: payload,
  });
  if (error) throw new Error(formatApiError(error));
  return data;
}

export async function getRoomMessages(roomId, limit = 30, beforeId = null) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await sb().rpc('get_room_messages', {
    p_room_id: roomId,
    p_limit: limit,
    p_before_id: beforeId,
  });
  if (error) throw new Error(formatApiError(error));
  return data || [];
}

export async function toggleChatMessageReaction(roomId, messageId, emoji) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('toggle_chat_message_reaction', {
    p_room_id: roomId,
    p_message_id: messageId,
    p_emoji: emoji,
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data[0] : data;
}

export async function getChatMessageReactions(roomId, limit = 100) {
  if (!isSupabaseConfigured || !roomId) return [];
  const { data, error } = await sb().rpc('get_chat_message_reactions', {
    p_room_id: roomId,
    p_limit: limit,
  });
  if (error) throw new Error(formatApiError(error));
  return data || [];
}

export async function getChatMessageReactionUsers(roomId, messageId, emoji) {
  if (!isSupabaseConfigured || !roomId || !messageId || !emoji) return [];
  const { data, error } = await sb().rpc('get_chat_message_reaction_users', {
    p_room_id: roomId,
    p_message_id: messageId,
    p_emoji: emoji,
  });
  if (error) throw new Error(formatApiError(error));
  return data || [];
}

export async function deleteChatMessage(messageId) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('delete_chat_message', {
    p_message_id: messageId,
  });
  if (error) throw new Error(formatApiError(error));
  return data ?? true;
}

export async function markRoomRead(roomId, lastMessageId) {
  if (!roomId || !lastMessageId) return;
  const { error } = await sb().rpc('mark_room_read', {
    p_room_id: roomId,
    p_last_message_id: lastMessageId,
  });
  if (error) throw new Error(formatApiError(error));
  dispatchChatUnreadRefresh();
}

export async function getBusinessInbox(businessId, options = {}) {
  if (!isSupabaseConfigured || !businessId) return [];
  const { data, error } = await sb().rpc('get_business_inbox', {
    p_business_id: businessId,
    p_item_filter: options.itemFilter || null,
    p_member_filter: options.memberFilter || null,
    p_status_filter: options.statusFilter || null,
    p_limit: options.limit || 20,
    p_cursor_at: options.cursorAt || null,
    // 카카오채널 관리자 패리티 필터 — 전달 안 하면 기본값(진행중/전체 등)으로 동작
    p_room_status_filter: options.roomStatusFilter === undefined ? 'active' : options.roomStatusFilter,
    p_important_only: options.importantOnly || false,
    p_label_filter: options.labelFilter || null,
    p_answered_filter: options.answeredFilter || null,
  });
  if (error) throw new Error(formatApiError(error));
  return data || [];
}

/** 채팅방 "중요" 표시 토글 */
export async function toggleChatRoomImportant(roomId, important) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('toggle_chat_room_important', {
    p_room_id: roomId,
    p_important: Boolean(important),
  });
  if (error) throw new Error(formatApiError(error));
}

/** 채팅방 내부 메모(직원끼리만 보는 메모) 저장 */
export async function setChatRoomMemo(roomId, memo) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('set_chat_room_memo', {
    p_room_id: roomId,
    p_memo: memo || '',
  });
  if (error) throw new Error(formatApiError(error));
}

/** 상담완료 처리 (담당자 이상 권한) */
export async function resolveChatRoom(roomId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('resolve_chat_room', { p_room_id: roomId });
  if (error) throw new Error(formatApiError(error));
}

/** 채팅방 차단/차단 해제 (매니저 이상 권한) */
export async function setChatRoomBlocked(roomId, blocked) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('set_chat_room_blocked', {
    p_room_id: roomId,
    p_blocked: Boolean(blocked),
  });
  if (error) throw new Error(formatApiError(error));
}

/** 내 계정(담당 직원 기준)에서만 채팅방을 목록에서 내보내기 — 다른 직원 화면엔 계속 남음 */
export async function leaveBusinessChatRoom(roomId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('leave_business_chat_room', { p_room_id: roomId });
  if (error) throw new Error(formatApiError(error));
}

/** 업체 채팅방 라벨 목록 */
export async function listChatLabels(businessId) {
  if (!isSupabaseConfigured || !businessId) return [];
  const { data, error } = await sb().rpc('list_chat_labels', { p_business_id: businessId });
  if (error) throw new Error(formatApiError(error));
  return data || [];
}

/** 라벨 생성/수정 (labelId 있으면 수정, 없으면 신규) → 라벨 id 반환 */
export async function upsertChatLabel(businessId, name, { labelId = null, color = '#8B5CF6' } = {}) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('upsert_chat_label', {
    p_business_id: businessId,
    p_name: name,
    p_label_id: labelId,
    p_color: color,
  });
  if (error) throw new Error(formatApiError(error));
  return data;
}

export async function deleteChatLabel(labelId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('delete_chat_label', { p_label_id: labelId });
  if (error) throw new Error(formatApiError(error));
}

/** 채팅방에 붙은 라벨을 통째로 교체 */
export async function setChatRoomLabels(roomId, labelIds) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('set_chat_room_labels', {
    p_room_id: roomId,
    p_label_ids: Array.isArray(labelIds) ? labelIds : [],
  });
  if (error) throw new Error(formatApiError(error));
}

/** 채팅방 담당자 지정 (매니저 이상 권한). memberId=null이면 담당자 해제 용도로도 사용 가능하게 열어둠. */
export async function assignChatRoom(roomId, memberId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('assign_chat_room', {
    p_room_id: roomId,
    p_member_id: memberId,
  });
  if (error) throw new Error(formatApiError(error));
}

export async function getChatRoom(roomId) {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await sb().rpc('get_chat_room', { p_room_id: roomId });
  if (error) throw new Error(formatApiError(error));
  return data;
}

export async function joinBusinessChatRoom(roomId) {
  if (!roomId) return;
  const { error } = await sb().rpc('join_business_chat_room', { p_room_id: roomId });
  if (error) throw new Error(formatApiError(error));
}

/** @param {string} businessId */
export async function isBusinessMember(businessId) {
  if (!businessId || !isSupabaseConfigured) return false;
  const { data, error } = await sb().rpc('is_business_member', { p_business_id: businessId });
  if (error) return false;
  return Boolean(data);
}

export async function getMyChatRooms(limit = 50) {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await sb().rpc('get_my_chat_rooms', { p_limit: limit });
  if (error) throw new Error(formatApiError(error));
  return data || [];
}

/** Sum unread_count across B chat rooms for header badge. */
export async function getMyChatUnreadTotal(limit = 50) {
  const rooms = await getMyChatRooms(limit);
  return rooms.reduce((sum, row) => sum + (Number(row.unread_count) || 0), 0);
}

/** Partitioned chat_messages: Realtime fires on child tables, not only the parent. */
export const CHAT_MESSAGE_REALTIME_TABLES = [
  'chat_messages',
  'chat_messages_y2026m05',
  'chat_messages_y2026m06',
  'chat_messages_y2026m07',
  'chat_messages_y2026m08',
  'chat_messages_y2026m09',
  'chat_messages_y2026m10',
  'chat_messages_y2026m11',
  'chat_messages_y2026m12',
  'chat_messages_y2027m01',
  'chat_messages_y2027m02',
  'chat_messages_y2027m03',
  'chat_messages_y2027m04',
  'chat_messages_y2027m05',
  'chat_messages_y2027m06',
];

export function chatMessageRealtimeTableNames(referenceDate = new Date()) {
  const names = new Set(CHAT_MESSAGE_REALTIME_TABLES);
  const base = new Date(Date.UTC(referenceDate.getUTCFullYear(), referenceDate.getUTCMonth(), 1));
  for (let offset = -1; offset <= 1; offset += 1) {
    const d = new Date(base);
    d.setUTCMonth(d.getUTCMonth() + offset);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    names.add(`chat_messages_y${y}m${m}`);
  }
  return [...names];
}

/**
 * @param {string} roomId
 * @param {(row: object) => void} onMessage
 * @param {(status: string, err?: Error) => void} [onStatus]
 */
export function subscribeToRoom(roomId, onMessage, onStatus) {
  const client = getSupabase();
  if (!client || !roomId) return () => {};

  const filter = `room_id=eq.${roomId}`;
  const channel = client.channel(`chat_room:${roomId}`);

  const onInsert = (payload) => {
    if (payload?.new && onMessage) onMessage(payload.new);
  };

  for (const table of chatMessageRealtimeTableNames()) {
    channel.on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table, filter },
      onInsert,
    );
  }

  channel.subscribe((status, err) => {
    if (onStatus) onStatus(status, err ?? undefined);
  });

  return () => {
    void client.removeChannel(channel);
  };
}

/**
 * Inbox refresh trigger for v2 chats (ChatInboxProvider only — do not call elsewhere).
 * Watches participant unread changes for current user.
 */
export function subscribeToMyChatInboxV2(myUserId, onEvent) {
  const client = getSupabase();
  if (!client || !myUserId || !onEvent) return () => {};

  const channel = client.channel(`inbox_v2:${myUserId}`);
  channel.on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'chat_participants', filter: `user_id=eq.${myUserId}` },
    () => onEvent(),
  );
  channel.subscribe();

  return () => {
    void client.removeChannel(channel);
  };
}
